import { forwardRef } from 'react';
import ArrowDropDown from '@mui/icons-material/ArrowDropDown';
import Cancel from '@mui/icons-material/Cancel';
import DataUsageIcon from '@mui/icons-material/DataUsage';

import { Chip } from './Chip';
import type { ChipSize, ChipColor } from './Chip';
import { ChipAddon } from './ChipAddon';
import { Colors } from 'styles';

export type FilterChipProps = {
  size?: ChipSize;
  disabled?: boolean;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  className?: string;
  hasSelection?: boolean;
  hasMenu?: boolean;
  isLoading?: boolean;
  children: React.ReactNode;
  onClear?: () => void;
  selectionColor?: ChipColor;
  hideClear?: boolean;
  disableOverlay?: boolean;
};

export const FilterChip = forwardRef(function ForwardedFilterChip(props: FilterChipProps, ref) {
  const {
    size = 'md',
    disabled = false,
    hasSelection = false,
    hasMenu = false,
    isLoading = false,
    children,
    onClick,
    onClear,
    className,
    selectionColor = 'gray',
    hideClear = false,
    disableOverlay = false,
    ...rest
  } = props;
  const handleClear = (e: React.MouseEvent<SVGSVGElement>) => {
    e.stopPropagation();
    onClear?.();
  };

  return (
    <Chip
      {...rest}
      border={hasMenu ? 'square' : 'rounded'}
      // @ts-expect-error [EN-7967] - TS2322 - Type 'ForwardedRef<unknown>' is not assignable to type 'LegacyRef<HTMLButtonElement>'.
      ref={ref}
      color={hasSelection ? selectionColor : 'gray'}
      variant={hasSelection ? 'solid' : 'outline'}
      size={size}
      disabled={disabled}
      onClick={onClick}
      className={className}
      disableOverlay={disableOverlay}
    >
      {children}
      <ChipAddon>
        {hasSelection && !hideClear ? (
          <Cancel
            sx={{
              '&:hover': {
                color: Colors.gray9,
              },
            }}
            data-testid="close-filter-chip"
            onClick={disabled ? undefined : handleClear}
          />
        ) : isLoading ? (
          /* Had issues with CircularProgress and its conflicting css. Using this as alternative for now */
          <span data-testid="loading-filter-chip-icon">
            <DataUsageIcon
              sx={{
                color: Colors.blue4,
                animation: 'spin 2s linear infinite',
                '@keyframes spin': {
                  '100%': {
                    transform: 'rotate(360deg)',
                  },
                },
              }}
            />
          </span>
        ) : hasMenu ? (
          <ArrowDropDown
            data-testid="dropdown-filter-chip-arrow"
            sx={{ transform: 'scale(1.2)' }}
          />
        ) : null}
      </ChipAddon>
    </Chip>
  );
});
